import { Component, Ref, Watch } from 'vue-property-decorator'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import ShipmentsSearcher from '@/modules/shipments-module/components/searcher/shipments-searcher.vue'
import GridHeader from '@/components/GridHeader.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import ShipmentsModalSend from '@/modules/shipments-module/components/modal-send/shipments-modal-send.vue'
import { BaseCrud } from '@/shared/models/cruds.models'
import PrintComponent from '@/modules/printModule/PrintComponent.vue'
import ShipmentsModalSendScript from '@/modules/shipments-module/components/modal-send/shipments-modal-send'
import {
    initialShipmentsGridState,
    ShipmentsGridReturningTypes,
    ShipmentsGridState
} from '@/modules/shipments-module/components/grid/shipments-grid.model'
import {
    shipmentsGridDatasourceFactory,
    ShipmentsGridType
} from '@/modules/shipments-module/components/grid/datasources/shipments-grid-datasource-factory'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import {
    FilterChangedEvent,
    GetContextMenuItemsParams,
    GridReadyEvent,
    MenuItemDef
} from '@ag-grid-enterprise/all-modules'
import { ReportGridRequest } from '@/api/services/common.dto'
import {
    ShipmentItemResponse,
    ShipmentRequestResponse
} from '@/modules/shipments-module/services/shipments.dto'
import { intersectionBy, uniq } from 'lodash'
import { shipmentsItemStatusChangerService } from '@/modules/shipments-module/services/item/item-status-changer/shipments-item-status-changer.service'
import { shipmentsRequestExporterService } from '@/modules/shipments-module/services/request/request-exporter/shipments-request-exporter.service'
import { shipmentsItemExporterService } from '@/modules/shipments-module/services/item/item-exporter/shipments-item-exporter.service'
import { shipmentsReportService } from '@/modules/shipments-module/services/report/shipments-report.service'
import { shipmentsRequestCancellerService } from '@/modules/shipments-module/services/request/request-canceller/shipments-request-canceller.service'
import { reportType } from '@/modules/printModule/print-component.model'
import { shipmentRequestState } from '@/api/resources/entityState/shipment-request-state'
import { AxiosResponse } from 'axios'
import {
    CommonExportType,
    ExportCommonParams
} from '@/api/resources/common/shared/models/common.models'
import { GridUtils } from '@/shared/util/grid-utils'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import { store } from '@/store/store'
import { ShipmentsStoreHelper } from '@/modules/shipments-module/store/shipments-store.helper'
import ModalConfirmationCheckable from '@/components/modals/ModalConfirmationCheckable.vue'
import { ShipmentsSearcherResponse } from '@/modules/shipments-module/components/searcher/shipments-searcher.model'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'
import { ModalUtils } from '@/shared/util/modal-utils'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { StoreUtils } from '@/shared/util/store-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { ShipmentsUtil } from '@/modules/shipments-module/shared/utils/shipments.util'

@Component({
    components: {
        ShipmentsSearcher,
        GridHeader,
        GridDisplayOptions,
        VtGrid,
        PrintComponent,
        ShipmentsModalSend,
        ModalConfirmationCheckable
    }
})
export default class ShipmentsGrid extends BaseCrudServerSideComponent<ShipmentsGridReturningTypes> implements BaseCrud {
    @Ref() readonly printComponent!: any
    @Ref() readonly shipmentsModalSend!: ShipmentsModalSendScript
    @Ref() readonly shipmentsGridConfirmationModal!: any

    state: ShipmentsGridState

    /// GRID COMPUTED ///

    private get _hasAnySelected(): boolean {
        return !!this.state.selectedRows.length
    }

    // shipment modes
    private static get _gridPendingToSent(): boolean {
        return ShipmentsStoreHelper.gridType === ShipmentsGridType.PENDING_TO_SENT
    }
    private static get _gridPendingToReceive(): boolean {
        return ShipmentsStoreHelper.gridType === ShipmentsGridType.PENDING_TO_RECEIVE
    }
    private get _isShipmentResponse(): boolean {
        if (this.state.selectedRows.length) {
            return this.state.selectedRows[0].hasOwnProperty('shipmentRequest')
        }
        return false
    }

    private static _getShipmentRequest(entity: ShipmentsGridReturningTypes): ShipmentRequestResponse {
        if ('shipmentRequest' in entity) { // ShipmentItemResponse
            return entity.shipmentRequest
        }
        return entity
    }

    // shipment request
    private get _uniqueDestinationLaboratory(): boolean {
        if (!this._isShipmentResponse) {
            const selectedRows = this.state.selectedRows as ShipmentRequestResponse[]
            if (selectedRows.length) {
                const destinationLabIds = uniq(selectedRows.map(selectedRow => selectedRow.destinationLaboratory.labId))
                return destinationLabIds.length === 1
            }
        }
        return false
    }

    /// shipment response
    private get _deliveryNoteIds(): number[] {
        if (this._isShipmentResponse) {
            const selectedRows = this.state.selectedRows as ShipmentItemResponse[]
            if (selectedRows?.length) {
                return uniq(selectedRows.map(selectedRow => selectedRow.shipment.id))
            }
        }
        return []
    }
    private get _uniqueDeliveryNoteId(): boolean {
        return this._deliveryNoteIds.length === 1
    }
    private get _deliveryNoteId(): number | null {
        return this._uniqueDeliveryNoteId ? this._deliveryNoteIds[0] : null
    }

    get canSend(): boolean {
        return this._hasAnySelected &&
            ShipmentsGrid._gridPendingToSent &&
            this._uniqueDestinationLaboratory
    }
    get canMark(): boolean {
        return this._hasAnySelected &&
            ShipmentsGrid._gridPendingToReceive &&
            this._uniqueDeliveryNoteId
    }
    get canPrint(): boolean {
        return this._hasAnySelected &&
            !ShipmentsGrid._gridPendingToSent &&
            this._uniqueDeliveryNoteId
    }

    get visibleCancelButton(): boolean {
        return ShipmentsGrid._gridPendingToSent
    }

    get disabledCancelButton(): boolean {
        return !this.state.selectedRows.length
    }

    get sidebarSelectedOptionUiComponent(): string | undefined {
        return ShipmentsStoreHelper.sidebarSelectedOption?.uicomponent ??
            ShipmentsStoreHelper.sidebarSelectedParentOption?.uicomponent
    }
    get sidebarSelectedOptionId(): string {
        return `${ShipmentsStoreHelper.sidebarSelectedOption?.id}`
    }
    @Watch('sidebarSelectedOptionId')
    private _onSidebarSelectedOptionChanged(): void {
        this.state.gridOptions.rowModelType = AgGridRowModelType.SERVER_SIDE
        this.state.rowData = []
    }

    get gridTitle(): string {
        const label = ShipmentsStoreHelper.sidebarSelectedOption
            ? this.$tc(ShipmentsStoreHelper.sidebarSelectedOption.label)
            : ''
        const parentLabel = ShipmentsStoreHelper.sidebarSelectedParentOption
            ? this.$tc(ShipmentsStoreHelper.sidebarSelectedParentOption.label)
            : null

        if (parentLabel) {
            return `${parentLabel} - ${label}`
        }
        return label
    }
    get gridSubtitle(): string {
        if (!this._hasAnySelected) {
            return ''
        }

        const choice = this.state.selectedRows.length > 1 ? 2 : 1
        return `${this.state.selectedRows.length} ${this.$tc('global.selectedElements', choice)}`
    }
    get rowModel(): string {
        return this.state.gridOptions.rowModelType ?? ''
    }

    constructor() {
        super()
        this.state = initialShipmentsGridState()
        if (this.sidebarSelectedOptionUiComponent) {
            this.state.gridUiComponent = this.sidebarSelectedOptionUiComponent
        }
    }

    /// VUE HOOKS ///

    async mounted(): Promise<void> {
        StoreUtils.cleanSearchState()

        // load the states needed for the grid shipment request status column filter
        await PromiseUtils.toCallUnique(shipmentRequestState.loadStates())
    }

    /// AG GRID HOOKS ///

    /** @override */
    onGridReady(event: GridReadyEvent): void {
        this.state.gridApi = event.api
        this.state.columnApi = event.columnApi

        // load shipments dynamic datasource service and column definition
        const datasource =
            shipmentsGridDatasourceFactory.getDatasource(
                store.state.labId,
                ShipmentsStoreHelper.gridType)
        if (datasource) {
            this.state.datasource = datasource
            this.state.gridApi.setColumnDefs(datasource.columnDefs)
        }

        if (this.state.gridOptions.rowModelType === AgGridRowModelType.SERVER_SIDE) {
            if (this.state.datasource) {
                this.state.gridApi.setFilterModel(ShipmentsStoreHelper.filterModel)
                this.state.gridApi.setServerSideDatasource(this.state.datasource)
            }
        } else {
            this.state.gridApi.setRowData(this.state.rowData ?? [])
            this.state.gridApi.selectAll()
        }
    }

    /** @override */
    onFilterChanged({ api }: FilterChangedEvent) {
        api.deselectAll()
        ShipmentsStoreHelper.filterModel = api.getFilterModel()
    }

    private async _reloadGrid(): Promise<void> {
        this.state.gridOptions.rowModelType = AgGridRowModelType.SERVER_SIDE
        this.$nextTick(() => this.reloadData())
        await ShipmentsUtil.loadSummaries()
    }

    /// SERVICE CALLS ///

    private async _markReceived(destinationLaboratoryId: number, shipmentItemIds: EntityIdentifiersRequest): Promise<void | null> {
        const ids = shipmentItemIds?.entityIdentifiers
        if (ids?.length) {
            const response = await PromiseUtils.toCallUnique(shipmentsItemStatusChangerService.markReceived(destinationLaboratoryId, shipmentItemIds))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('shipments.toastMessages.markReceived'))
                return response
            }
        }
        return null
    }
    private async _markNotReceived(destinationLaboratoryId: number, shipmentItemIds: EntityIdentifiersRequest): Promise<void | null> {
        const ids = shipmentItemIds?.entityIdentifiers
        if (ids?.length) {
            const response = await PromiseUtils.toCallUnique(shipmentsItemStatusChangerService.markNotReceived(destinationLaboratoryId, shipmentItemIds))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('shipments.toastMessages.markNotReceived'))
                return response
            }
        }
        return null
    }

    private _printOrderReport(laboratoryId: number, shipmentId: number): Promise<Blob | null> {
        return PromiseUtils.toCallSecureLoading(
            shipmentsReportService.getShippingOrderReport(laboratoryId, shipmentId, true),
            error => ModalUtils.showErrorModalFromErrorResponse(error, this.$tc('print.modals.errorReport')))
    }

    /// GRID EVENT HANDLERS ///

    onClearFilter(): void {
        this.state.gridApi?.setFilterModel(null)
        ShipmentsStoreHelper.filterModel = {}
        // this.state.columnApi?.resetColumnState()
    }

    onSend(): void {
        const selectedRows = this.state.selectedRows as ShipmentRequestResponse[]
        this.shipmentsModalSend.show(selectedRows)
    }

    async onSent(): Promise<void> {
        await this._reloadGrid()
    }

    async onReceived(): Promise<void> {
        const selectedRows = this.state.selectedRows as ShipmentItemResponse[]
        const request: EntityIdentifiersRequest = {
            entityIdentifiers: selectedRows.map(selectedRow => selectedRow.id)
        }
        if (await this._markReceived(store.state.labId, request) !== null) {
            await this._reloadGrid()
        }
    }

    async onNotReceived(): Promise<void> {
        const selectedRows = this.state.selectedRows as ShipmentItemResponse[]
        const request: EntityIdentifiersRequest = {
            entityIdentifiers: selectedRows.map(selectedRow => selectedRow.id)
        }
        if (await this._markNotReceived(store.state.labId, request) !== null) {
            await this._reloadGrid()
        }
    }

    async onPrint(): Promise<void> {
        if (this._deliveryNoteId) {
            await this.printComponent.printReport(
                reportType.REQUEST_REPORT,
                () => this._printOrderReport(store.state.labId, this._deliveryNoteId!))
        }
    }

    async exportReport(exportType: CommonExportType): Promise<AxiosResponse<Blob> | null> {
        const columns = this.getColumnsToExport()
        if (columns && this.state.gridApi) {
            const selectedRows = this.state.selectedRows as ShipmentItemResponse[]
            const selectedIds = selectedRows.map(selectedRow => selectedRow.id)
            const entityIdentifiersRequest: EntityIdentifiersRequest = {
                entityIdentifiers: selectedIds
            }
            const request: ReportGridRequest = {
                sectionLabel: this.gridTitle,
                columns,
                entityIdentifiersRequest,
                filterModel: this.state.gridApi.getFilterModel()
            }
            const params: ExportCommonParams = {
                sort: GridUtils.getSortValue(this.state.gridApi.getSortModel()),
                exportType
            }
            switch (ShipmentsStoreHelper.gridType) {
            case ShipmentsGridType.PENDING_TO_SENT:
                return shipmentsRequestExporterService.exportPendingToBeSent(store.state.labId, request, params)
            case ShipmentsGridType.PENDING_TO_RECEIVE:
                return shipmentsItemExporterService.exportPendingToBeReceived(store.state.labId, request, params)
            case ShipmentsGridType.SENT:
                return shipmentsItemExporterService.exportSent(store.state.labId, request, params)
            case ShipmentsGridType.RECEIVED:
                return shipmentsItemExporterService.exportReceived(store.state.labId, request, params)
            }
        }
        return null
    }

    async onSearchSuccess(entitiesSelected: ShipmentsSearcherResponse[]): Promise<void> {
        const entities: ShipmentsGridReturningTypes[] = []
        entitiesSelected.forEach(entitySelected => {
            if ('id' in entitySelected) { // ShipmentItemResponse
                entities.push(entitySelected)
            } else if ('shipmentRequestResponse' in entitySelected) { // ShipmentRequestAndItemResponse
                entities.push(entitySelected.shipmentItemResponse ?? entitySelected.shipmentRequestResponse)
            }
        })

        if (this.state.rowData) {
            if (intersectionBy(this.state.rowData, entities, 'id').length) {
                ModalUtils.showWarningModal(this.$tc('component.searcher.error.elementAlreadyAdded'))
            } else {
                this.state.rowData.push(...entities)

                if (this.state.gridOptions.rowModelType === AgGridRowModelType.CLIENT_SIDE) {
                    if (this.state.gridApi) {
                        const previousNodes = this.state.gridApi.getRenderedNodes()

                        this.state.gridApi.setRowData(this.state.rowData)
                        if (this.state.rowData.length > 1) {
                            entities.forEach(entity => {
                                if (this.state.gridApi) {
                                    GridUtils.keepSelectionGridWhenSearch(
                                        this.state.gridApi,
                                        entity.id,
                                        previousNodes)
                                }
                            })
                        }
                    }
                } else {
                    this.state.gridOptions.rowModelType = AgGridRowModelType.CLIENT_SIDE
                }
            }
        }
    }

    private _showInGlobalLocator(label: string): void {
        this.$eventHub.$emit('show-in-global-locator', label)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): (string | MenuItemDef)[] {
        const menuItems: (string | MenuItemDef)[] = []

        if (this.canSend) {
            menuItems.push({
                name: this.$tc('shipments.actions.send'),
                action: this.onSend
            })
        }

        if (this.canMark) {
            menuItems.push({
                name: this.$tc('shipments.actions.received'),
                action: this.onReceived
            })
            menuItems.push({
                name: this.$tc('shipments.actions.notReceived'),
                action: this.onNotReceived
            })
        }

        if (this.canPrint) {
            menuItems.push({
                name: this.$tc('shipments.actions.printDeliveryNote'),
                action: this.onPrint
            })
        }

        if (this.state.selectedRows.length === 1) {
            const entitySelected = this.state.selectedRows[0]
            const shipmentRequest = ShipmentsGrid._getShipmentRequest(entitySelected)

            menuItems.push({
                name: this.$tc('global.showInGlobalLocator'),
                action: () => this._showInGlobalLocator(shipmentRequest.entityLabel)
            })
        }

        return menuItems
    }

    async loadModalConfiguration(): Promise<void> {
        const modalState = store.getters.modalsState[this.state.modalConfig.storeName]
        if (!modalState) {
            const customConfig = await customConfigResource.get(this.state.modalConfig.uiComponent)
            if (customConfig) {
                await StoreUtils.setModalConfirmationState(this.state.modalConfig.storeName, customConfig.config)
            }
        }
    }

    getModalConfirmationState(nameModal: string) {
        return StoreUtils.getModalConfirmationState(this.state.modalConfig.storeName, nameModal)
    }

    async cancelShipmentRequest(): Promise<void> {
        const request = {
            entityIdentifiers: this.state.selectedRows.map(row => row.id)
        }
        const laboratoryId = store.state.labId
        const response = await PromiseUtils.toCallUnique(shipmentsRequestCancellerService.cancel(request, laboratoryId))
        if (response) {
            ToastUtils.showToastSuccess()
            this._reloadGrid()
        }
    }

    async onCancelButtonClick(): Promise<void> {
        await this.loadModalConfiguration()
        if (!this.getModalConfirmationState(this.state.modalConfig.name)) {
            this.shipmentsGridConfirmationModal.show(() => {
                this.cancelShipmentRequest()
            })
        } else {
            await this.cancelShipmentRequest()
        }
    }
}
